// lib
import { useContext, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { DataContext } from '@aytm/app/stats/page/question-views/components/questions-mapping/Component'

import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'

// components
import { InputText } from 'primereact/inputtext'

import ImagesDropdown from '@aytm/app/stats/components/images-dropdown/ImagesDropdown'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'

import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/questions-mapping/styles/Component.module.scss'

const Entity = ({ item, position, imagesDisabled, dndItemsList }) => {
  const { id, mode, data, disabled, dispatch } = useContext(DataContext)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: PageGlobals.WINDOW_UID,
      remove: () =>
        dispatch({
          type: 'DELETE_ENTITY',
          data: position - 1
        })
    }
  }, [item, position, id, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-entity-${PageGlobals.WINDOW_UID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-entity-${PageGlobals.WINDOW_UID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ENTITY',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onImageChange = value => {
    // Build the next entity from a copy — never mutate the reducer-owned item in place
    const updated = { ...item }
    if ('off' == value) delete updated.image
    else updated.image = value

    dispatch({
      type: 'UPDATE_ENTITY',
      data: {
        item: updated,
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ENTITY',
      data: position - 1
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ENTITY',
      data: {
        item: {
          ...item,
          pattern: '',
          key: generateUID()
        },
        position: position
      }
    })
  })

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  const renderAdvanced = () => (
    <>
      <div className={`${style.images} p-fluid p-col`}>
        <InputText
          className={classNames('p-inputtext-rounded', {
            'p-composite-right': !disabled,
            'p-inputtext-with-image': !imagesDisabled
          })}
          placeholder="Please type a label..."
          value={decodeText(item.text)}
          maxLength={120}
          disabled={disabled}
          onChange={disabled ? null : onLabelChange}
        />
        {imagesDisabled ? null : (
          <ImagesDropdown image={item.image} options={data.images} disabled={disabled} onImageChange={onImageChange} />
        )}
      </div>
    </>
  )
  const renderBasic = () => (
    <>
      <div className="p-fluid p-col-4">
        <InputText
          className="p-inputtext-rounded p-composite-right"
          aria-label="Data source"
          value={item.pattern}
          disabled
        />
      </div>
      <div className={`${style.images} p-fluid p-col`}>
        <InputText
          className={classNames('p-inputtext-rounded', {
            'p-composite': !disabled,
            'p-composite-left': disabled,
            'p-inputtext-with-image': !imagesDisabled
          })}
          placeholder="Please type a label..."
          value={decodeText(item.text)}
          maxLength={120}
          disabled={disabled}
          onChange={disabled ? null : onLabelChange}
        />
        {imagesDisabled ? null : (
          <ImagesDropdown image={item.image} options={data.images} disabled={disabled} onImageChange={onImageChange} />
        )}
      </div>
    </>
  )

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']}`}>
      {disabled ? null : (
        <span className={style.delete} aria-label="Delete entity" {...pressable(onDeleteClick)}>
          <i className="icon-aytmfd-x-v2" />
        </span>
      )}
      {/* keyed by position: the list's drag handlers rewrite this cell's text outside React, so a new
          position mounts a fresh node rather than updating text that is no longer on screen */}
      <div key={position} className={style.index}>
        {position}.
      </div>
      <div className={`${style.group} ${style['group-av']}${disabled ? ' ' + style['group-av-disabled'] : ''}  p-col`}>
        {disabled || 'advanced' == mode ? renderAdvanced() : renderBasic()}
      </div>
      {disabled ? null : <span ref={dndListItemHandleRef} className={style.handle} onClick={onHandleClick} />}
    </div>
  )
}

Entity.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  imagesDisabled: PropTypes.bool,
  dndItemsList: PropTypes.any
}

Entity.displayName = 'Entity'

export default Entity
